import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { v1Api } from '../../../services/api';
import hospitalService from '../../../services/hospitalService';
import PaymentAmountDisplay from '../components/PaymentAmountDisplay';

/**
 * PaymentOrderCreationModal Component
 * Supports both Hospital and Fleet dashboards
 * Endpoints:
 *   - Hospital: POST /v1/hospital/payments/create-order
 *   - Fleet: POST /v1/fleet/payments/create-order
 * Purpose: Manual payment order creation
 */
const PaymentOrderCreationModal = ({
  isOpen,
  onClose,
  bookingId: initialBookingId,
  bookingData,
  onOrderCreated,
  onError,
  dashboardType = 'hospital' // 'hospital' or 'fleet'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingId, setBookingId] = useState(initialBookingId || '');
  const [amount, setAmount] = useState('');
  const [currency] = useState('INR');
  const [notes, setNotes] = useState('');
  const [razorpayScriptLoaded, setRazorpayScriptLoaded] = useState(false);
  
  // Update bookingId from API response (initialBookingId or bookingData) when modal opens
  // Booking ID is strictly from API response and cannot be edited
  useEffect(() => {
    if (isOpen) {
      // Priority: initialBookingId > bookingData.booking_id > bookingData.id
      const apiBookingId = initialBookingId || 
                          bookingData?.booking_id || 
                          bookingData?.id || 
                          '';
      if (apiBookingId) {
        setBookingId(apiBookingId);
      }
    }
  }, [initialBookingId, bookingData, isOpen]);

  // Load Razorpay script
  useEffect(() => {
    if (isOpen && !razorpayScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        setRazorpayScriptLoaded(true);
      };
      document.body.appendChild(script);
    }
  }, [isOpen, razorpayScriptLoaded]);

  // Initialize amount from booking data (API response)
  // Amount is strictly from API response and cannot be edited
  useEffect(() => {
    if (isOpen && bookingData) {
      const fare = bookingData.estimated_fare || 
                   bookingData.estimatedFare || 
                   bookingData.final_fare || 
                   bookingData.finalFare ||
                   bookingData.fare ||
                   bookingData.amount;
      if (fare) {
        let fareValue = fare;
        if (typeof fareValue === 'string') {
          fareValue = parseFloat(fareValue.replace(/[₹,\s]/g, ''));
        } else if (typeof fareValue === 'object' && fareValue?.toString) {
          fareValue = parseFloat(fareValue.toString());
        }
        if (!isNaN(fareValue) && fareValue > 0) {
          setAmount(fareValue.toFixed(2));
        }
      }
    }
  }, [bookingData, isOpen]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      requestAnimationFrame(() => {
        document.body.style.overflow = 'unset';
      });
    };
  }, [isOpen, loading, onClose]);

  const handleCreateOrder = async () => {
    if (!bookingId || !amount) {
      const errMsg = 'Please provide booking ID and amount.';
      setError(errMsg);
      if (onError) onError(errMsg);
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      const errMsg = 'Please enter a valid amount.';
      setError(errMsg);
      if (onError) onError(errMsg);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare request body according to endpoint specification
      // Request body: { amount, currency, bookingId, notes: { BookingId } }
      const requestBody = {
        amount: amountValue, // Number format (e.g., 1185.07)
        currency: currency, // "INR"
        bookingId: bookingId, // String booking ID
        notes: {
          BookingId: bookingId // Always use BookingId in notes
        }
      };

      console.log('💳 [PaymentOrderCreationModal] Creating payment order for booking:', bookingId);
      console.log('💳 [PaymentOrderCreationModal] Dashboard type:', dashboardType);
      console.log('💳 [PaymentOrderCreationModal] Request body:', requestBody);

      let result;

      // Call the appropriate endpoint based on dashboard type
      if (dashboardType === 'fleet') {
        // Fleet endpoint: POST /v1/fleet/payments/create-order
        const endpoint = '/v1/fleet/payments/create-order';
        console.log('💳 [PaymentOrderCreationModal] Using Fleet endpoint: POST', endpoint);
        const response = await v1Api.post(endpoint, requestBody);
        result = response.data || response;
      } else {
        // Hospital endpoint: POST /v1/hospital/payments/create-order
        console.log('💳 [PaymentOrderCreationModal] Using Hospital endpoint via hospitalService');
        result = await hospitalService.createPaymentOrder(requestBody);
      }

      console.log('💳 [PaymentOrderCreationModal] Full response:', result);

      // Validate response structure
      // Expected response:
      // {
      //   "success": true,
      //   "message": "Payment order created successfully",
      //   "data": {
      //     "order": {
      //       "id": "order_RsEfbGF5Qgd79U",
      //       "amount": 1185.07,
      //       "currency": "INR",
      //       "receipt": "order_1765877767232_5f3c2a",
      //       "status": "created"
      //     },
      //     "keyId": "rzp_test_RbYLX9Fx48IwWy"
      //   }
      // }
      if (!result?.success) {
        const errorMsg = result?.message || result?.error || 'Payment order creation failed';
        throw new Error(errorMsg);
      }

      if (!result?.data) {
        throw new Error('Invalid response: missing data field');
      }

      const orderData = result.data.order;
      const keyId = result.data.keyId;

      if (!orderData) {
        throw new Error('Invalid response: missing order information');
      }

      if (!orderData?.id) {
        throw new Error('Invalid response: missing order ID');
      }

      if (!keyId) {
        throw new Error('Invalid response: missing Razorpay key ID');
      }

      console.log('💳 [PaymentOrderCreationModal] Order created:', orderData.id);
      console.log('💳 [PaymentOrderCreationModal] Order amount:', orderData.amount);
      console.log('💳 [PaymentOrderCreationModal] Order currency:', orderData.currency);
      console.log('💳 [PaymentOrderCreationModal] Order status:', orderData.status);
      console.log('💳 [PaymentOrderCreationModal] Razorpay Key ID:', keyId);

      // Open Razorpay checkout
      openRazorpayCheckout(orderData, keyId);
    } catch (err) {
      console.error('❌ [PaymentOrderCreationModal] Payment order creation error:', err);
      console.error('❌ [PaymentOrderCreationModal] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errMsg = err.response?.data?.message || 
                    err.response?.data?.error || 
                    err.message || 
                    'Failed to create payment order. Please try again.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
    }
  };

  const openRazorpayCheckout = (orderData, keyIdToUse) => {
    if (!window.Razorpay) {
      const errMsg = 'Razorpay SDK not loaded. Please refresh the page.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
      return;
    }

    const handlePaymentSuccess = (response) => {
      console.log('✅ [PaymentOrderCreationModal] Payment successful:', response);
      setLoading(false);
      if (onOrderCreated) {
        onOrderCreated({
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
          orderId: orderData.id,
          orderData: orderData
        });
      }
      onClose();
    };

    const onPaymentDismiss = () => {
      setLoading(false);
      const errMsg = 'Payment was cancelled. Please try again.';
      setError(errMsg);
      if (onError) onError(errMsg);
    };

    // Note: orderData.amount is in rupees (not paise) based on the response format
    // Razorpay expects amount in paise, so we need to convert
    const orderAmountInPaise = Math.round(parseFloat(orderData.amount) * 100);

    const options = {
      key: keyIdToUse,
      amount: orderAmountInPaise, // Amount in paise (converted from rupees)
      currency: orderData.currency || 'INR',
      name: 'Ambulance Service',
      description: `Payment for Booking ${bookingId}`,
      order_id: orderData.id,
      handler: handlePaymentSuccess,
      theme: {
        color: '#667eea'
      },
      modal: {
        ondismiss: onPaymentDismiss
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('❌ [PaymentOrderCreationModal] Error opening Razorpay checkout:', err);
      const errMsg = 'Failed to open payment gateway. Please try again.';
      setError(errMsg);
      setLoading(false);
      if (onError) onError(errMsg);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="bg-green-50 p-4 rounded-t-lg border-b border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-green-100 p-1.5 rounded-full">
                <CreditCard className="w-4 h-4 text-green-600" />
              </div>
              <div className="ml-2">
                <h3 className="text-base font-semibold text-green-900">
                  Create Payment Order
                </h3>
                <p className="text-xs text-green-700">
                  Create a new payment order {dashboardType === 'fleet' ? '(Fleet)' : '(Hospital)'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-green-400 hover:text-green-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Booking ID *
              </label>
              <input
                type="text"
                value={bookingId}
                readOnly
                placeholder="Enter booking ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (INR) *
              </label>
              <input
                type="number"
                value={amount}
                readOnly
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
              {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
                <div className="mt-2">
                  <PaymentAmountDisplay amount={amount} size="small" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">Note</p>
                <p className="text-blue-600">
                  After creating the order, Razorpay checkout will open automatically for payment.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 rounded-b-lg">
          <div className="flex justify-between items-center gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateOrder}
              disabled={loading || !bookingId || !amount || !razorpayScriptLoaded}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-colors flex items-center justify-center gap-2 ${
                loading || !bookingId || !amount || !razorpayScriptLoaded
                  ? 'bg-green-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Order...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Create Payment Order
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentOrderCreationModal;

